// src/main/shell/symbolGrid/search/SearchAutocomplete.ts
// The SYMBOL adapter over the shared, generic TreeAutocomplete. The Discord-style
// dropdown logic now lives ONCE in @vitesse/base-ui/search (shared with
// the log tree); this file just maps the grid's symbol data accessor
// (`getSymbolData()` → AutocompleteData) onto the generic options + keeps the
// exact constructor/method surface SymbolGrid already calls, so the grid is
// unchanged.

import { SEARCH_COMMANDS, symbolTextMatch } from './searchCommands';
import type { FilterContext } from './searchCommands';
import type { FlatSymbol } from '../types';
import { TreeAutocomplete } from '@vitesse/base-ui/search';

/**
 * The data the autocomplete needs to suggest real values — a controller-scoped
 * accessor bag (unchanged).
 */
export interface AutocompleteData {
  iterate(): Iterable<{ symbol_id: string; type: string; permission: string }>;
  getLabel(symbolId: string): string | undefined;
  getValue(symbolId: string): boolean | number | string | null | undefined;
}

export interface SearchAutocompleteOptions {
  /** See TreeAutocompleteOptions.inlineGhost — off for a host whose input paints
   *  its own text (the model binding picker). */
  inlineGhost?: boolean;
  onSelect: (completion: string) => void;
  onClose: () => void;
  getSymbolData: () => AutocompleteData | null;
}

/** Build a minimal FlatSymbol from an iterated entry (only the fields the
 *  command matchers + suggestValue read). */
function toFlatSymbol(
  data: AutocompleteData,
  entry: { symbol_id: string; type: string; permission: string },
): FlatSymbol {
  return {
    id: entry.symbol_id,
    symbol_id: entry.symbol_id,
    type: entry.type,
    permission: entry.permission,
    parentId: null,
    level: 0,
    hasChildren: false,
    isExpanded: false,
    isVisible: true,
    path: [entry.symbol_id],
    formattedPermission: entry.permission,
    label: data.getLabel(entry.symbol_id),
    value: data.getValue(entry.symbol_id),
  };
}

/**
 * Thin symbol-bound wrapper over the generic TreeAutocomplete. Keeps the exact
 * `new SearchAutocomplete(container, { onSelect, onClose, getSymbolData })`
 * surface + update/handleKeyDown/isOpen/hide/destroy methods SymbolGrid uses.
 */
export class SearchAutocomplete {
  private inner: TreeAutocomplete<FlatSymbol, FilterContext>;

  constructor(container: HTMLElement, options: SearchAutocompleteOptions) {
    this.inner = new TreeAutocomplete<FlatSymbol, FilterContext>(container, {
      commands: SEARCH_COMMANDS,
      ctx: () => ({ getLabel: (id) => options.getSymbolData()?.getLabel(id) }),
      iterate: () => {
        const data = options.getSymbolData();
        if (!data) return null;
        return (function* () {
          for (const entry of data.iterate()) yield toFlatSymbol(data, entry);
        })();
      },
      valueForCommand: (cmd, item) => cmd.suggestValue?.(item),
      textMatch: symbolTextMatch,
      onSelect: options.onSelect,
      onClose: options.onClose,
      inlineGhost: options.inlineGhost,
    });
  }

  update(inputValue: string, cursorPos: number, anchorRect: DOMRect): void {
    this.inner.update(inputValue, cursorPos, anchorRect);
  }

  handleKeyDown(e: KeyboardEvent): boolean {
    return this.inner.handleKeyDown(e);
  }

  isOpen(): boolean {
    return this.inner.isOpen();
  }

  hide(): void {
    this.inner.hide();
  }

  destroy(): void {
    this.inner.destroy();
  }
}
